import { getAnalytics } from "firebase/analytics"
import { FirebaseOptions, initializeApp } from "firebase/app"
import { getAuth } from "firebase/auth"

import { CONFIGURATION } from "@env/variables"

interface UIResolvedTenant {
    firebaseTenantId: string
    label?: string
    logo?: string
    provider?: string
    name?: string
}

const firebaseConfig: FirebaseOptions = {
    apiKey: CONFIGURATION.firebase.apiKey,
    authDomain: CONFIGURATION.firebase.authDomain,
    projectId: "dengenlabs",
    appId: "1:265888598630:web:6e5e76c8ecde887e5afba7",
}

const firebaseApp = initializeApp(firebaseConfig)
const firebaseAuth = getAuth(firebaseApp)
const analytics = getAnalytics(firebaseApp)

export async function setFirebaseTenant(tenantEmail?: string) {
    if (!tenantEmail) {
        return
    }

    try {
        let retries = 3
        let retryDelay = 250

        while (retries > 0) {
            try {
                const response = await fetch("/api/resolve-tenant", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        tenantEmail: tenantEmail,
                    }),
                    signal: AbortSignal.timeout(5000),
                })

                if (!response) {
                    throw new Error("No response received from tenant API")
                }

                if (!response.ok) {
                    try {
                        const errorData = await response.json()
                        console.error("Failed to resolve tenant ID:", errorData.error)
                    } catch (parseError) {
                        console.error(`Failed to resolve tenant ID: HTTP ${response.status} - ${parseError}`)
                    }

                    if (response.status === 404) {
                        console.warn(`Tenant not found for ${tenantEmail}`)
                        return
                    }

                    throw new Error(`HTTP error ${response.status}`)
                }

                const data = (await response.json()) as UIResolvedTenant

                if (data && data.firebaseTenantId) {
                    firebaseAuth.tenantId = data.firebaseTenantId
                    CONFIGURATION.firebase.providerType = data.provider ?? "oidc"
                    return data
                } else {
                    console.error(`Invalid response format, missing tenantId for ${tenantEmail}`)
                    return
                }
            } catch (fetchError) {
                if (retries > 1) {
                    console.warn(`Tenant resolution failed, retrying in ${retryDelay}ms...`, fetchError)
                    await new Promise((resolve) => setTimeout(resolve, retryDelay))
                    retryDelay *= 2
                    retries--
                } else {
                    throw fetchError
                }
            }
        }
    } catch (error) {
        console.error("Error setting Firebase tenant:", error instanceof Error ? error.message : "Unknown error")
    }
}

async function getFirebaseAuthToken(refresh?: boolean) {
    const user = firebaseAuth.currentUser
    if (user) {
        return user
            .getIdToken(refresh)
            .then((token) => {
                return token
            })
            .catch((err) => {
                console.error("Failed to get access token", err)
                return null
            })
    } else {
        return Promise.resolve(null)
    }
}

function signOut() {
    return firebaseAuth.signOut()
}

export { analytics, firebaseApp, firebaseAuth, firebaseConfig, getFirebaseAuthToken, signOut }
